/**
 * Buying from the site (owner, 2026-10-05: "if someone clicks to buy on a pricing page or module on our landing page it should
 * take them through the app to stripe and get them signed up, cc on tap"). One link shape, used by the landing site's buttons
 * and read by the web app:
 *
 *   https://app.cleancleaver.com/app/?signup=1&buy=1&plan=<band>&state=<ST>&src=<where>&addon=<key>
 *
 * - `buy=1` marks the buyer path: sign in, a short "Set up to buy" step, then Stripe Checkout (docs/PAYMENTS.md "Buying from the site").
 * - `plan` is the band the visitor tapped. A HINT for presets and words only: the price is always computed on the server from
 *   the business (core planFor via services/api billing.ts createCheckout). A client never sends a price.
 * - `state` presets the business's state (a state page's buttons). `src` names the button's page or module, for the funnel
 *   counts (`pricing`, `home`, `state-home`, a feature or module id). `addon` is an add-on module's key.
 * Anything unknown is dropped, never trusted: a bad `plan` is no plan, a bad `state` no state.
 */
import type { PlanBand } from "./billing.ts";
import { SUPPORTED_STATES, type UsState } from "./states/types.ts";

export const BUY_BANDS: readonly PlanBand[] = ["solo", "small", "team", "kitchen", "building"];
/** The add-on modules a site button can carry (core payments.ts PriceKey add-ons). */
export const BUY_ADDONS = ["ai_photos", "ai_roomcheck"] as const;
export type BuyAddon = (typeof BUY_ADDONS)[number];

/** What a buy link carries. Every field is optional: `buy=1` alone is a plain "start your 25 days" from a page. */
export interface BuyIntent { plan?: PlanBand; state?: UsState; src?: string; addon?: BuyAddon }

/** A source label: short, lower-case words and dashes (a page or module id). Anything else is dropped. */
export const BUY_SRC_RE = /^[a-z0-9][a-z0-9-]{0,47}$/;

const isBand = (v: string | null | undefined): v is PlanBand => !!v && (BUY_BANDS as readonly string[]).includes(v);
const isAddon = (v: string | null | undefined): v is BuyAddon => !!v && (BUY_ADDONS as readonly string[]).includes(v);
export function buyState(v: string | null | undefined): UsState | undefined {
  const s = (v ?? "").trim().toUpperCase();
  return (SUPPORTED_STATES as readonly string[]).includes(s) ? (s as UsState) : undefined;
}
export const buySrc = (v: string | null | undefined): string | undefined => (v && BUY_SRC_RE.test(v) ? v : undefined);

/** The query string of a buy link (no leading "?"), in a fixed order: signup, buy, plan, state, src, addon. */
export function buyQuery(i: BuyIntent = {}): string {
  const p = new URLSearchParams({ signup: "1", buy: "1" });
  if (isBand(i.plan)) p.set("plan", i.plan);
  const st = buyState(i.state);
  if (st) p.set("state", st);
  const src = buySrc(i.src);
  if (src) p.set("src", src);
  if (isAddon(i.addon)) p.set("addon", i.addon);
  return p.toString();
}

/** The intent in a URL's query, or null when it isn't a buy link (no `buy=1`). Unknown values are dropped. */
export function parseBuyIntent(search: string | URLSearchParams): BuyIntent | null {
  const p = typeof search === "string" ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search) : search;
  if (p.get("buy") !== "1") return null;
  const plan = p.get("plan"), addon = p.get("addon");
  const st = buyState(p.get("state")), src = buySrc(p.get("src"));
  return { ...(isBand(plan) ? { plan } : {}), ...(st ? { state: st } : {}), ...(src ? { src } : {}), ...(isAddon(addon) ? { addon } : {}) };
}

/** The buy parameters, so an app can take them off its address once kept (a reload never starts the path again). */
export const BUY_PARAM_NAMES = ["buy", "plan", "state", "src", "addon"] as const;

/* ─────────────── sample orgs ─────────────── */

/**
 * The seeded sample businesses (packages/data-memory seed.ts: "every id starts with demo-"). They never take a card: the checkout
 * and portal routes refuse them (owner, 2026-10-05: "if they try to pay on the Demo, they shouldn't be able to. They need to be
 * taken to their own personal account").
 */
export const SAMPLE_ORG_PREFIX = "demo-";
export const isSampleOrgId = (id: string | null | undefined): boolean => !!id && id.startsWith(SAMPLE_ORG_PREFIX);
